// ** Modules
import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';

// ** UI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import SearchOffIcon from '@mui/icons-material/SearchOff';

// ** Types
import type { SxProps, Theme } from '@mui/material/styles';

export type CardsGridProps = {
	children: React.ReactNode;
	sx?: SxProps<Theme>;
	animateOnView?: boolean;
	showAvailabilityCount?: boolean;
	availabilityLabelSuffix?: string;
	totalCount?: number;
	showEmptyState?: boolean;
	emptyState?: React.ReactNode;
	emptyStateTitle?: string;
	emptyStateMessage?: string;
};

const MotionBox = motion.create(Box);

const CardsGrid = ({
	children,
	sx,
	animateOnView = true,
	showAvailabilityCount = false,
	availabilityLabelSuffix = 'Verfügbar',
	totalCount,
	showEmptyState = true,
	emptyState,
	emptyStateTitle = 'Keine gefunden.',
	emptyStateMessage = 'Bitte passen Sie Ihre Suche oder Filter an.'
}: CardsGridProps) => {
	const reduceMotion = useReducedMotion();
	const items = React.Children.toArray(children);
	const displayCount = totalCount !== undefined ? totalCount : items.length;
	const isEmpty = items.length === 0;

	const defaultEmptyState = (
		<Box
			sx={{
				py: 10,
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				gap: 2,
				color: 'text.secondary'
			}}
		>
			<SearchOffIcon sx={{ fontSize: 56, opacity: 0.4 }} />
			<Typography variant="body-lg" sx={{ fontWeight: 500 }}>
				{emptyStateTitle}
			</Typography>
			<Typography variant="body-md" sx={{ opacity: 0.7 }}>
				{emptyStateMessage}
			</Typography>
		</Box>
	);

	return (
		<Box>
			{showAvailabilityCount ? (
				<Typography
					variant="body-md"
					sx={{
						mt: 12,
						mb: { xs: 6, md: 8 },
						color: 'common.black'
					}}
				>
					{`${displayCount} ${availabilityLabelSuffix}`}
				</Typography>
			) : null}

			{isEmpty && showEmptyState ? (emptyState ?? defaultEmptyState) : null}

			<MotionBox
				sx={[
					{
						display: 'grid',
						gridTemplateColumns: {
							xs: '1fr',
							sm: 'repeat(2, minmax(0, 1fr))',
							md: 'repeat(3, minmax(0, 1fr))',
							lg: 'repeat(3, minmax(0, 1fr))'
						},
						// Keep 3 columns in the desktop-mid range (992-1199px),
						// then move to 4 columns once there is enough horizontal space.
						'@media (min-width:1200px)': {
							gridTemplateColumns: 'repeat(4, minmax(0, 1fr))'
						},
						columnGap: 4,
						rowGap: 8,
						// On xs: cap each card at 361px and center it in the 1fr column.
						// On sm+: stretch to fill the column.
						'& > div': {
							width: { xs: 'min(100%, 361px)', sm: '100%' },
							minWidth: 0,
							justifySelf: { xs: 'center', sm: 'stretch' }
						},
						alignItems: 'start'
					},
					...(Array.isArray(sx) ? sx : sx ? [sx] : [])
				]}
			>
				{items.map((child, index) => (
					<motion.div
						layout
						key={(child as React.ReactElement).key}
						initial={animateOnView && !reduceMotion ? { opacity: 0, y: 10 } : false}
						whileInView={animateOnView && !reduceMotion ? { opacity: 1, y: 0 } : undefined}
						viewport={animateOnView && !reduceMotion ? { once: false, amount: 0 } : undefined}
						transition={
							animateOnView && !reduceMotion ? { duration: 0.28, ease: 'easeOut', delay: index * 0.06 } : undefined
						}
					>
						{child}
					</motion.div>
				))}
			</MotionBox>
		</Box>
	);
};

export default CardsGrid;
